// 시뮬레이션 상태 저장소. 렌더·UI는 shared/types.ts의 읽기 전용 뷰로만 읽는다.
import {
  AttackPhase, AwakenPhase, BossKind, BossState, HeroAct, SKILL_MAX, Scene, TeleShape, TELEGRAPH_CAPACITY,
  type AttackPhaseId, type AwakenPhaseId, type BossKindId, type BossStateId, type HeroActId, type SceneId, type TeleShapeId,
} from "../shared/types";
import { BOSS, HERO, MOB_CAPACITY, SKILLS } from "../shared/config";
import type { MapDef } from "../shared/levels";

/** Uint16 타이머 포화값. 0xffff는 hitTick의 '피격 없음' 표시로 남겨 둔다. */
export const TIMER_MAX = 0xfffe;

export class HeroState {
  x = 0; y = 0; z = 0; prevX = 0; prevY = 0; prevZ = 0;
  facing = Math.PI; act: HeroActId = HeroAct.Idle; actTick = 0;
  comboStep = 0; attackPhase: AttackPhaseId = AttackPhase.Windup; skillId = 0;
  hp: number = HERO.maxHp; maxHp: number = HERO.maxHp;
  mp: number = HERO.maxMp; maxMp: number = HERO.maxMp; level = 1;
  /** 스킬 MP 소모 감소 비율(유니크, 능력치에서 옮김, BALANCE-1028). 시전·HUD 가능 표시가 shared/gear effectiveMpCost로 쓴다 */
  mpCostCut = 0;
  invulnerable = false; dodgeCooldown = 0; awakenGauge = 0;
  /** 회피 캔슬 쿨(공격·스킬을 회피로 끊으면 DODGE.cancelCooldown에서 시작, COMBAT-FEEL 2.8) */
  dodgeCancelCooldown = 0;
  /** D3a 지역 상태 이상(영웅 틱): 서리 둔화 남은 틱, 중독 남은 틱 */
  chillTicks = 0; poisonTicks = 0;
  /** 스킬 1..SKILL_MAX(c022: 영웅별 스킬 수가 달라 12칸, 카엘은 0..4만 쓴다) */
  readonly cooldowns = new Float32Array(SKILL_MAX);
  readonly cooldownMax = new Float32Array(SKILL_MAX);
  constructor() { for (let i = 0; i < SKILLS.length; i++) this.cooldownMax[i] = SKILLS[i].cooldown; }
}

export class MobsState {
  readonly capacity = MOB_CAPACITY;
  /** Inactive가 아닌 슬롯 수(Spawning·Dying 포함) */
  aliveCount = 0;
  readonly state = new Uint8Array(MOB_CAPACITY); readonly kind = new Uint8Array(MOB_CAPACITY);
  readonly x = new Float32Array(MOB_CAPACITY); readonly y = new Float32Array(MOB_CAPACITY); readonly z = new Float32Array(MOB_CAPACITY);
  readonly prevX = new Float32Array(MOB_CAPACITY); readonly prevY = new Float32Array(MOB_CAPACITY); readonly prevZ = new Float32Array(MOB_CAPACITY);
  readonly facing = new Float32Array(MOB_CAPACITY); readonly hp = new Float32Array(MOB_CAPACITY); readonly maxHp = new Float32Array(MOB_CAPACITY);
  readonly stateTick = new Uint16Array(MOB_CAPACITY); readonly hitTick = new Uint16Array(MOB_CAPACITY).fill(0xffff);
  readonly elite = new Uint8Array(MOB_CAPACITY);
  readonly posture = new Float32Array(MOB_CAPACITY);
  readonly guardBroken = new Uint16Array(MOB_CAPACITY);
  readonly windupTicks = new Uint16Array(MOB_CAPACITY);
  /** 속박 남은 세계 틱(c022 그물 덫). 0보다 크면 추적·배회 이동을 하지 않는다(공격은 한다) */
  readonly rooted = new Uint16Array(MOB_CAPACITY);
  /** 스페셜 몬스터 표지(shared/specialMonster MobSpecial: 0 일반, 1 황금 고블린, 2 챔피언). 스폰마다 0으로 시작 */
  readonly special = new Uint8Array(MOB_CAPACITY);
  /** 챔피언 접두어 번호(CHAMPION_AFFIXES 인덱스, special이 2일 때만 뜻이 있다) */
  readonly affix = new Uint8Array(MOB_CAPACITY);
  /** 슬롯 세대. 스폰마다 1 증가(렌더가 슬롯 재사용을 감지) */
  readonly gen = new Uint16Array(MOB_CAPACITY);
}

export class BossStateView {
  state: BossStateId = BossState.Inactive; stateTick = 0;
  x = 0; z = 0; prevX = 0; prevZ = 0; facing = Math.PI;
  hp = 0; maxHp = 1; radius: number = BOSS.radius; hitTick = 0xffff; name: string = BOSS.name;
  invulnerable = false;
  /** 경직 게이지 0..1 */
  stagger = 0;
  /** BossKind(0 고목 수호자, 1 잿불 대주술사). 계약 자리표시: 게임 레인이 보스 종류별로 채운다 */
  kind: BossKindId = BossKind.Treant;
  /** D3a 보스 정의 번호(content/bosses BOSS_KEYS 순서, 렌더 색조·크기). 0 = 고목 수호자 */
  variant = 0;
  /** 페이즈 2(체력 phase2 이하)에 들어섰는지 */
  phase2 = false;
  /** S5 분노·광폭(game/bossRage BossRage): 0 없음, 1 분노(중간보스), 2 광폭(지역보스 페이즈 3) */
  rage = 0;
  /** 분노·광폭 체력 문턱(최대 체력 비율, 0 = 이 판에 없음). HUD 체력바 눈금 */
  rageAt = 0;
  /** 비행 높이 비율(레이드 비룡, game/raid가 쓴다) */
  air = 0;
}

export class Telegraph {
  active = false; shape: TeleShapeId = TeleShape.Circle;
  x = 0; z = 0; radius = 0; angle = 0; width = 0; tick = 0; total = 1;
}
export function makeTelegraphs(): Telegraph[] {
  const slots = new Array<Telegraph>(TELEGRAPH_CAPACITY);
  for (let i = 0; i < slots.length; i++) slots[i] = new Telegraph();
  return slots;
}

export class FlowState {
  scene: SceneId = Scene.Title; sceneTick = 0; mapId: MapDef["id"] = "village"; roomIndex = -1;
  readonly roomState = new Uint8Array(8); wave = 0; waveCount = 0; objectiveKilled = 0; objectiveTotal = 0;
  readonly gateOpen = new Float32Array(16); stageLabel = ""; objectiveLabel = ""; elapsedTicks = 0; paused = false;
  /** loadMap/retry마다 1 증가 */
  sceneSerial = 0;
  /** D3a: 지금 판 스테이지 id·종류, 지금 마을, 마을 이동 연출(도착·출발 마을, 진행 틱) */
  stageId = ""; stageKind = ""; town = 1;
  travelTo = 0; travelFrom = 0; travelTick = 0; travelTotal = 0;
  /** 클리어 연출 대기: 남은 틱(0 = 대기 없음)·전체 틱·카메라가 볼 마지막 처치 위치 */
  clearHold = 0; clearHoldTotal = 0; clearFocusX = 0; clearFocusZ = 0;
}

export class AwakenState {
  phase: AwakenPhaseId = AwakenPhase.None; phaseTick = 0; phaseTotal = 0; slashIndex = 0; usedThisRun = 0;
}

export class ResultState {
  resultId = ''; restored = false; savePending = false; saveRecovered = false;
  /** D3a: 깬 스테이지 id(자유 입장 = 빈 문자열), 첫 클리어 여부, 열린 다음 스테이지('' = 없음), 예약된 마을 이동(0 = 없음) */
  stageId = ''; firstClear = false; nextStage = ''; travelTo = 0;
  cleared = false; stars = 0; starMask = 0; clearTicks = 0; kills = 0; maxCombo = 0; damageDealt = 0; gold = 0;
  /** S3: 이번 판 클리어 골드·카드 배율, 첫 클리어 보상(골드는 gold에 이미 합쳐져 있다), 받은 장비('등급:정의 id') */
  clearMult = 1; firstGold = 0; firstStones = 0; firstStoneTier = 0;
  /** 이번 판 스페셜 몬스터 처치 골드(처치 순간 영속 지급 — gold에 들어 있지 않고 별도 항목으로 보인다) */
  specialGold = 0;
  readonly firstGear: string[] = [];
  /** 레이드 결과(RAID-PRD 6절, ResultView.raid…) */
  raid = ''; raidDiff = 'normal'; raidFirst = false; raidFirstGold = 0; raidFirstLegend = ''; raidFirstStones = 0; raidN = 0; raidLimit = 0; raidLeft = -1; raidSet = ''; raidLegend = ''; raidStones = 0; raidStoneTier = 0; raidProtect = 0;
  /** 일일 던전·탑 결과(ResultView.daily…) */
  dailyKind = ''; dailyFloor = 0; dailyNext = 0; dailyBest = 0; dailyStones = 0; dailyProtect = 0; dailyGems = 0;
  /** 넷 판: 보상 안내 한 줄(우편함) */
  raidNote = '';
  /** 용사의 시련 판 결과(C7, ResultView.trial…). trialId = 시련 판이 아니면 '' */
  trialId = ''; trialMet = false; trialNew = false; trialLow = 0; trialStones = 0; trialStoneTier = 0; trialChest = ''; jobReady = 0;
  readonly cards = new Int32Array(3).fill(-1);
  /** 카드 3장의 내용(영수증 cardLoot 문자열, content/cardReward). 골드 카드는 'g:<등급>', 이전 영수증은 ''(골드) */
  readonly cardLoot: string[] = ['', '', ''];
  pickedCard = -1;
  reset(): void {
    this.resultId = ''; this.restored = false; this.savePending = false; this.saveRecovered = false;
    this.stageId = ''; this.firstClear = false; this.nextStage = ''; this.travelTo = 0;
    this.cleared = false; this.stars = 0; this.starMask = 0; this.clearTicks = 0; this.kills = 0;
    this.maxCombo = 0; this.damageDealt = 0; this.gold = 0; this.cards.fill(-1); this.cardLoot.fill(''); this.pickedCard = -1;
    this.clearMult = 1; this.firstGold = 0; this.firstStones = 0; this.firstStoneTier = 0; this.firstGear.length = 0; this.specialGold = 0;
    this.raid = ''; this.raidDiff = 'normal'; this.raidFirst = false; this.raidFirstGold = 0; this.raidFirstLegend = ''; this.raidFirstStones = 0; this.raidN = 0; this.raidLimit = 0; this.raidLeft = -1; this.raidSet = ''; this.raidLegend = ''; this.raidStones = 0; this.raidStoneTier = 0; this.raidProtect = 0; this.raidNote = '';
    this.dailyKind = ''; this.dailyFloor = 0; this.dailyNext = 0; this.dailyBest = 0; this.dailyStones = 0; this.dailyProtect = 0; this.dailyGems = 0;
    this.trialId = ''; this.trialMet = false; this.trialNew = false; this.trialLow = 0; this.trialStones = 0; this.trialStoneTier = 0; this.trialChest = ''; this.jobReady = 0;
  }
}
